// THE PROVIDER, THE ACCOUNT'S KEY, OR THE MODEL'S ANSWER — one row per failure kind in this family.
// See `../failure-kind.ts` for how the four families become `TaskFailureKind`.

import { TaskHoldReason } from '../hold';
import {
  BabysitRoundCharge,
  charged,
  type FailureKindRow,
  PROVIDER_CAPACITY_RECHECK_MS,
  PROVIDER_KEY_REFUSED_RECHECK_MS,
  PROVIDER_TRANSIENT_BACKOFF_BASE_MS,
  PROVIDER_TRANSIENT_BACKOFF_CAP_MS,
  REVIEW_NO_MODEL_RECHECK_MS,
  TASK_MAX_ATTEMPTS,
  TaskExhaustionAction,
  uncharged,
  wire,
  wireUnproduced,
} from './row';

/**
 * THE PROVIDER, THE ACCOUNT'S KEY, OR THE MODEL'S ANSWER — the endpoint, the credential or the model refused, throttled or answered with something that is not an answer.
 *
 * Each row is the WHOLE decision for its kind — who writes it, what it costs, whether it
 * parks, whose fault it is and what a reader is told. The doc on each key is the kind's
 * own docblock followed by the reasoning that used to sit beside its entry in each of the
 * per-concern tables (`TASK_FAILURE_PRODUCER`, `TASK_FAILURE_BUDGET`, the box-local set,
 * the re-pend hold, `BABYSIT_ROUND_CHARGE`), moved verbatim.
 */
export const PROVIDER_FAILURE_KINDS = {
  /**
   * A provider answered 5xx — the model endpoint itself faulted. Split out of
   * {@link RunnerError} by slice 11 so the two can be budgeted apart: a box
   * fault is cleared by a fresh box, a provider fault is cleared by the provider
   * recovering, and only one of those is a reason to move the work. Same cap,
   * different cause, and the cause is what an operator reading `failure_kind`
   * needs. Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Since #4021 it also holds the TRANSPORT fault no wall claimed — a bare
   * `TimeoutError` with the run's own signal still live, and a 2xx whose body
   * the provider package could not read (`apps/runner/src/llm/transport-fault.ts`).
   * Neither is the account's or the box's; both remedy WAIT, exactly as the 5xx
   * does, which is why they share this member and not a fifth one.
   *
   * Producer:
   * A 5xx at the BYOK wire — the endpoint's own fault. Declared `WireUnproduced`
   * from 2026-08-29 ("nothing stamps the three provider codes") until plan 101's
   * runner-lifecycle slice 07 (2026-09-02): the AI SDK already retries a 5xx in-step
   * (`maxRetries`), so what reaches the grader is a PERSISTENT endpoint fault, and
   * filing that under the box sent the operator to the wrong subsystem. Same two
   * symbols as the other provider arms: one grades, one writes.
   *
   * A THIRD producer since #4021: the transport fault no wall claimed — Bun's bare
   * `TimeoutError` with the run's signal live, and a 2xx whose body the provider
   * package could not read. No status to grade and no body to quote, so neither
   * grader named in this Producer section could name it and it left the seam uncoded (`runner_error`, the
   * last two live shapes in that bucket). Wraps rather than stamps, because a
   * `DOMException`'s `code` is a read-only numeric accessor.
   *
   * Babysit round:
   * A provider 5xx — the endpoint faulted, not the work.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   */
  ProviderError: {
    value: 'provider_error',
    ordinal: 1,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
      'apps/runner/src/llm/transport-fault.ts#ProviderTransportError',
      'apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The model provider's endpoint returned an error. Nothing on the box or in the repository is wrong; it clears when the provider recovers.",
      unclaimed:
        'No box claimed this — the model provider’s endpoint was returning errors when the row was offered.',
      ran: 'The model provider’s endpoint returned an error. Nothing on the box or in the repository is wrong; it clears when the provider recovers.',
    },
  },
  /**
   * The provider REFUSED for an account condition — a rate limit, a quota, a
   * spend ceiling. THE TASK DID NOTHING WRONG AND PRODUCED NOTHING, so it is not
   * charged an attempt ({@link TaskBudgetKind.Uncharged}): the attempt counter
   * bounds *"the fix didn't work"*, never *"no fix was produced"*.
   *
   * This member is the whole point of slice 11's Part A. Before it, an
   * account-level limit landed in the {@link RunnerError} bucket and burned both
   * retry attempts of task after task in ninety seconds — an ACCOUNT condition
   * charged to every TASK that happened to be in flight while it held.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}); a producer
   * that emits no code cannot reach it, and that is deliberate — see
   * {@link classifyTaskFailure} on why there is no free-text heuristic.
   *
   * UNCHARGED IS SAFE ONLY BEHIND A PACER, and the pacer is the box (#3050): the
   * run that grades this also holds that box's next coding pull until the reset
   * the router stated, so the re-pend lands on a fleet that waits rather than one
   * that re-derives the same refusal every fifteen seconds.
   *
   * Producer:
   * THE THROTTLE, and the last kind to leave the unproduced set (2026-09-03, #3050).
   *
   * It sat `WireUnproduced` from 2026-08-29 for a reason worth keeping in one
   * sentence: its Uncharged budget is safe ONLY behind a pacer, and the only pacer
   * that existed (`no_usable_keys`, a hold on a cooled pool key) cannot reach the
   * coding lane, which runs on the BOX's own backend and carries no pool key at all.
   * A producer landing without one would have turned a bounded `runner_error` ladder
   * into an unbounded uncharged re-queue — the #2567 loop through a third door.
   *
   * What made it legitimate is the pacer landing WITH it: `providerFailureCode`
   * grades the router's pool-cooling prose (`N of N accounts in pool … rate limited
   * …, earliest reset <instant>` — 105 tasks in the 7 days to 2026-09-03, every one
   * `runner_error` and charged three attempts against a wall dated five minutes out),
   * and the box that raised it holds its own coding pulls until that instant
   * (`apps/runner/src/backend-cooldown.ts`, floored and capped) — so the uncharged
   * re-pend lands on a box that waits, not one that spins. A BARE 429 is still
   * uncoded: the status alone does not establish a throttle, only the prose that
   * names the pool and the reset does.
   *
   * Budget:
   * NOT CHARGED, and this single line is the fix the slice exists for: the run
   * produced nothing because the ACCOUNT hit a limit, and a counter that bounds
   * "the fix didn't work" must not be spent by "no fix was produced". It re-pends
   * HELD on `provider_capacity` (`taskFailureRependHold`); the provider's own reset
   * — or `PROVIDER_CAPACITY_RECHECK_MS` when it states none — is what paces the
   * retry. WHAT BOUNDS THE HOT LOOP is that park plus two pacers. The box that graded it holds its own pulls
   * until that reset (#3050, `apps/runner/src/backend-cooldown.ts`); and since #3783
   * the ACCOUNT is held at the claim gate: the event sink arms a pool cooldown off
   * the refusal's own quoted reset (`coolBackend`, `apps/worker/src/wiring/
   * nats-consumers.ts`), every box refuses the account until it lapses, and its
   * pending rows read `TaskHoldReason.PoolCooling` — then ONE box probes the pool
   * (`apps/worker/src/dispatch/key-runtime.ts`, floored and capped). So this is
   * already "park with the provider's retry_at", not a cap, and measured on prod
   * 2026-09-20/21 it does what it says: one probe per quoted window (~6 min, the
   * router's `(estimated)` reset), not a hot loop. `TaskHoldReason.NoUsableKeys` is
   * NOT that pacer — the coding lane runs on the box's own backend and carries no
   * pool key for the assigner to hold on.
   *
   * Park:
   * The capacity arm shares the key arms' stated-reset shape (the pool quotes the
   * same kind of reset), on its OWN reason and its own backstop — the pool's
   * recovery is minutes, not the key park's six hours. A capacity refusal never
   * waits for a key, so it is a DIFFERENT hold from `provider_key_refused`.
   *
   * Babysit round:
   * Throttled — an account condition, paced by the box.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   *
   * Alert sentence:
   * THE POOL-REFUSAL ARM (#4517).
   * `provider_rate_limit` has ONE producer and it grades exactly ONE sentence — the
   * shared upstream router's pool aggregate, behind `hasLlmThrottleMarker` +
   * `llmThrottleResetAt` (`packages/agent/src/providers/grade.ts#providerFailureCode`;
   * `TASK_FAILURE_PRODUCER` pins that no other writer exists). That sentence reports the
   * state of EVERY member of a shared pool, and its clauses carry three different
   * remedies: a cooling member waits, a member out of credit needs a top-up, a member a
   * human disabled needs a human.
   *
   * MEASURED on prod over the 7 days to 2026-09-20 (`task_run_events`, `kind='error'`,
   * `payload->>'code' = 'provider_rate_limit'` — 2,345 events over 92 tasks): ALL 2,345
   * carried `N more account out of credits and needs a top-up (…)`, 205 of them also
   * carried `N more needs a human (… disabled)`, and all 2,345 quoted an `earliest
   * reset`. The reset clears the cooling members and reaches neither of the other two,
   * so the previous wording — "Nothing to fix — the work re-enters the queue and runs
   * once the limit lifts" — was false on 100% of the population, in the sentence an
   * alert puts in front of somebody at 3am. Same argument #4556 made for `run_looping`:
   * naming the wall a rate limit names a ceiling that was never the wall.
   *
   * Reason sentence:
   * #4517: this kind has ONE producer and it grades exactly ONE sentence — the shared
   * router's pool aggregate (`providerFailureCode`, behind `hasLlmThrottleMarker` +
   * `llmThrottleResetAt`; `TASK_FAILURE_PRODUCER` pins that there is no other writer).
   * That sentence reports EVERY member of a shared pool, and its clauses carry three
   * different remedies: a cooling member waits, a member out of credit needs a top-up,
   * a member a human disabled needs a human. Measured on prod over the 7 days to
   * 2026-09-20 — 2,345 `provider_rate_limit` error events over 92 tasks — ALL 2,345
   * carried `N more account out of credits and needs a top-up (…)` and 205 also carried
   * `N more needs a human (… disabled)`. The quoted `earliest reset` clears the cooling
   * members and reaches neither of the other two, so the previous wording ("Nothing to
   * fix — the work re-enters the queue once the limit lifts") was false on 100% of the
   * population and sent the reader to buy concurrency headroom for a wallet. Same
   * argument #4556 made for `run_looping`: naming the wall a rate limit names a
   * ceiling that, for at least one pool member, was never the wall.
   *
   * It does NOT claim which member is in which state: this mapping is given the kind
   * and the attempt, never the message. It names the three states and sends the reader
   * to the one place that does say — the router's sentence, which `task outcome` and
   * the run timeline both carry verbatim.
   */
  ProviderRateLimit: {
    value: 'provider_rate_limit',
    ordinal: 2,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
    ),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.ProviderCapacity,
      backstopMs: PROVIDER_CAPACITY_RECHECK_MS,
      honorsStatedReset: true,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The account’s shared router pool refused this run and quoted a reset. It is not a throttle alone — the same sentence also reports pool accounts that are out of credit and need a top-up, and accounts a human has to re-enable, and the quoted reset clears neither. The run timeline’s error carries the per-account breakdown.',
      unclaimed:
        'No box claimed this — the account’s shared router pool was refusing work when the row was offered. Its own sentence names which pool accounts are merely cooling, which are out of credit and need a top-up, and which need a human to re-enable them; the run timeline’s error carries it verbatim.',
      ran: 'The account’s shared router pool refused the run and quoted a reset, so the work re-enters the queue with no attempt charged. Do not read that as a throttle alone: the same sentence reports pool accounts that are out of credit and need a top-up, and accounts a human has to re-enable, and the quoted reset clears neither. The run timeline’s error carries the per-account breakdown.',
    },
  },
  /**
   * The provider refused for an account-level quota — a settled spend ceiling,
   * not the throttling that {@link ProviderRateLimit} carries. Distinct from
   * {@link RunnerError} because no throw fires and the wire returns a code,
   * distinct from {@link ProviderRateLimit} because the cause is "spent" rather
   * than "paced", and the two members let an operator reading `failure_kind`
   * tell them apart without inspecting a free-text message. Budgeted
   * `Uncharged`, and the re-pend PARKS on `TaskHoldReason.ProviderKeyRefused`
   * until a key change or the re-check (`taskFailureRependHold`, plan 101 L3).
   * It was `Charged / 1 / Fail` until then, and that failed a spent account's
   * whole backlog one task at a time.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * 308 occurrences in the three days to 2026-08-29, every one filed
   * `runner_error`. Keyed on HTTP STATUS at the BYOK wire (402, a 4xx carrying an
   * exhaustion marker, or a 429 that `classifyLlmFailure` grades exhausted) — not
   * on the vendor sentence that made the population visible.
   *
   * TWO SYMBOLS, ONE FILE, and the pair is the reachability fix of 2026-08-29:
   * `providerFailureCode` GRADES, `stampProviderFailure` WRITES. The grader had a
   * single call site (`structuredObject`'s `ask`) while `harness/loop.ts` (the
   * box's only coding lane) and `harness/grounding.ts` (the scout survey and the
   * team coordinator) both drive `generateText` directly and graded nothing at
   * all — so every refusal either one met arrived uncoded and
   * {@link classifyTaskFailure}, which grades on `code` alone, filed it
   * `runner_error`. Those two lanes are NOT listed here: they call the stamper and
   * assign no code themselves, so naming them would point a reader at a file that
   * stamps nothing and would fail this table's own reachability check. Grep
   * `stampProviderFailure` for the call sites; the stamp is here.
   * Beside `apps/runner/src/guards.ts#stallDetector`: #2996: the guard's own classification. A trip whose run's last observed
   * provider interaction was a quota refusal carries this kind on the GUARD
   * TRIP and settles as it — stamped INDIRECTLY: the guard passes the kind into
   * `fire(...)` and `GuardAbortError` reads it back off the trip
   * (`this.code = trip.code ?? TaskFailureKind.RunTimeout`). There is no
   * `const` anywhere for that value — it is an ARGUMENT at a call site — so
   * the constant arm `assignsCode` grew cannot resolve it and this row is
   * declared by hand. Two producers since 2026-09-03 (`runtimeCeiling`
   * deleted): `stallDetector` classifies a silent run, `startupDeadline` one
   * that never produced a first observation.
   *
   * Budget:
   * NOT CHARGED, and the re-pend is HELD (plan 101 L3). This is the right answer
   * that this entry's 2026-08-29 note said it could not reach yet. At `Charged / 1 /
   * Fail` a spent key failed the account's backlog ONE TASK AT A TIME: 446 rows over
   * the 30 days to 2026-09-21, 227 of them on one account on 2026-09-05, every one a
   * task that would have run once the key was funded. The spend is an ACCOUNT
   * condition, and the attempt counter bounds *"the fix didn't work"*, not *"no fix
   * could be attempted"*.
   *
   * WHAT KEEPS THIS FROM BEING THE #2567 LOOP (an uncharged re-queue straight back
   * onto the key that was just refused) is the PARK: {@link taskFailureRependHold}
   * lands the row on `TaskHoldReason.ProviderKeyRefused` with a `hold_until`, the
   * claim skips it while the window is live, and a key turning `valid` on the account
   * releases it. So the hot loop is bounded by the hold, with no cap: at most one
   * re-run per {@link PROVIDER_KEY_REFUSED_RECHECK_MS} without a key change. The
   * 2026-08-29 argument for ONE attempt was that a weekly window cannot clear between
   * two IMMEDIATE re-pends. It still holds, and it is exactly why the re-pend is no
   * longer immediate.
   *
   * The tokens up to the refusal WERE billed. `Uncharged` makes no claim about cost:
   * metering records the tokens, and this budget decides only whether the task's
   * ladder moved. Unbounded, like `review_no_model`: a `maxConsecutive` would end
   * the task on a count of waits for a human to fund a key. That bound names only
   * the wait, not a failure (`docs/standards/agent-budgets.md`).
   *
   * Babysit round:
   * Old table group: the provider or the account's key: nothing about the PR decides these.
   * The key is over its quota/limit — the #2501/#2503 evidence; clears on reset or a key change.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   *
   * Alert sentence:
   * The PROVIDER, not the task, is why the run ended (plan 101 L3). None of these is a
   * fact about the work, so none charges an attempt:
   *
   * - a refused KEY (`provider_quota_exhausted`, `provider_auth_invalid`) used to charge
   *   one attempt and fail, so a spent quota failed the whole backlog one task at a time
   *   (446 rows in 30 days, 227 on one account on 2026-09-05). It now re-pends PARKED on
   *   `provider_key_refused`, and {@link ladderPhrase} says the row waits for a key;
   * - `provider_transient` is an outage that outlasted the box's own retries. It
   *   re-pends uncharged, HELD on `provider_capacity` until the reset the provider
   *   dated, else on a backoff that grows with the uncharged streak (#6501).
   *
   * Each sentence says what the human does; the ladder clause says what the row does.
   */
  ProviderQuotaExhausted: {
    value: 'provider_quota_exhausted',
    ordinal: 19,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
      'apps/runner/src/guards.ts#stallDetector',
      'apps/runner/src/guards.ts#startupDeadline',
    ),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.ProviderKeyRefused,
      backstopMs: PROVIDER_KEY_REFUSED_RECHECK_MS,
      honorsStatedReset: true,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The model provider refused: this account's quota is spent. Most of these are a WEEKLY limit, so wait for the window to reset or top the plan up — nothing about this task is wrong.",
      unclaimed: 'No box claimed this — the account’s quota was exhausted.',
      ran: 'The model provider refused across the ladder — the quota is spent. Most of these are a weekly limit, so wait for the window to reset or top the plan up — retrying now buys nothing.',
    },
  },
  /**
   * The provider answered HTTP 200 in the completion shape and it was NOT AN
   * ANSWER: a substituted or withdrawn model served, prose arrived where a tool
   * call was forced, prose arrived where a JSON object was demanded, or the
   * response carried no content at all. The repo's own "a 200 is not an answer"
   * defect class, given a name a query can count.
   *
   * Named at the provider seam by `nonAnswerGuardMiddleware`
   * (`packages/agent/src/providers/non-answer.ts`), which reads only STRUCTURAL
   * facts of the wire — which model served, which shape was demanded, whether
   * content arrived — and never the response prose. That is what keeps it out of
   * the free-text heuristic {@link classifyTaskFailure} refuses by design.
   *
   * Measured: `stealth/ox-alpha` on openrouter.ai ended its preview and answered
   * a farewell paragraph for 291 runs / 672 error events on
   * `acc_c76b7ed5391547f982ad03ccac750769` (tesote/tesote.ai), 2026-08-27/28.
   * CHARGED — the tokens were billed.
   *
   * THE #2711 GAP IS CLOSED (2026-08-29). This paragraph carried a caveat for
   * the kind's whole life — that the producer existed on the `packages/agent`
   * lane ONLY, so a box-run task reaching a non-answering provider was still
   * written `runner_error`. `apps/runner/src/llm/non-answer.ts` now raises
   * the same kind on the fleet's own wire, and {@link TASK_FAILURE_PRODUCER}
   * names both files. The caveat is deleted rather than left standing: a comment
   * describing a gap somebody has since closed is the same defect as one
   * claiming a guarantee the code never had, and it sends the next reader to
   * build a seam that already exists.
   *
   * Distinct from {@link ProviderError} (a 5xx — the endpoint said it failed) and
   * from {@link ModelProducedNoWork} (the model answered on-protocol and the
   * answer contained no work). Here the transport reported success and the
   * envelope was wrong.
   *
   * IT SPLITS THE STRUCTURED-OUTPUT THROW WITH {@link ToolUnavailable}, and the
   * split is measured rather than assumed: over the 158 prod rows carrying
   * `structured tool call failed schema validation` in the 30 days to
   * 2026-08-29, 110 are a model calling a tool it was never given
   * (`tool_unavailable` — provision the tool) and 48 are a model calling a tool
   * it HAS with arguments that fail the schema (this kind — the envelope was
   * wrong). The daily mix inverts completely, so a single label for both is
   * wrong for whichever arm is running that week.
   *
   * Producer:
   * BOTH LANES, as of 2026-08-29 — the `packages/agent` middleware and the
   * fleet's own box-side wire. The second entry closes #2711, which this table
   * carried as an open gap for as long as the kind existed: a box-run task that
   * reached a non-answering provider was written `runner_error` because no seam
   * on that lane named it. Two files, one kind, because the operator move is the
   * same — find out which model actually served.
   *
   * Budget:
   * CHARGED, and this one is not just "today's value carried over": the tokens
   * were BILLED. A withdrawn model that answers a farewell paragraph is paid for
   * exactly like a real completion, so the run cost the customer money and the
   * ladder must end. `Uncharged` would re-queue on a credential the holder knows
   * is valid — the #2567 loop, reached through the provider door.
   *
   * Babysit round:
   * A 200 that was not an answer (substituted/withdrawn model) — the provider's fault.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   */
  ProviderNonAnswer: {
    value: 'provider_non_answer',
    ordinal: 26,
    producer: wire(
      'packages/agent/src/providers/non-answer.ts#ProviderNonAnswerError',
      'apps/runner/src/llm/non-answer.ts#ProviderNonAnswerError',
      'apps/runner/src/structured-object.ts#structuredObject',
      'apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The model provider answered successfully and it was not an answer — a substituted or withdrawn model served, or the response carried no usable content. Check which model this run actually reached.',
      unclaimed:
        'No box claimed this — the model provider that would have served it answered with non-content before any run started.',
      ran: 'The model provider answered successfully and it was not an answer — a substituted or withdrawn model served, or the response carried no usable content. Check which model this run actually reached.',
    },
  },
  /**
   * The provider REFUSED THE IDENTITY: the credential is invalid, deleted,
   * revoked, or names an account that does not exist. Kimi answers this as
   * `planner error: User not found.` — 162 occurrences in the three days to
   * 2026-08-29, 93 of them on the last day, every one on the dogfood account
   * `acc_6dc71a28f22a45ec930c2b6bbaabcf4a`, and every one filed `runner_error`.
   *
   * The whole provider-side family exists to separate WHAT AN OPERATOR MUST DO,
   * and this arm's answer is the only one that is not "wait":
   *   {@link ProviderRateLimit}      — throttled. Wait; the pacer clears it.
   *   {@link ProviderQuotaExhausted} — spent. Wait for the window, or buy more.
   *   {@link ProviderError}          — the endpoint faulted. Wait; it recovers.
   *   `provider_auth_invalid`        — RE-KEY. Nothing clears on its own.
   * Distinct from {@link GitAuth}, which is the same class of fact about the
   * GIT credential, not the model one — different secret, different fix, and a
   * box can hold one valid and one dead.
   *
   * Budgeted `Uncharged` with a HELD re-pend on `TaskHoldReason.ProviderKeyRefused`
   * (plan 101 L3). A retry cannot help BY CONSTRUCTION, because the second attempt
   * presents the credential the first was refused for. So the row waits for the
   * re-key that CAN help, which releases it, instead of failing and making the
   * operator re-fire it after re-keying. See `TASK_FAILURE_BUDGET`.
   *
   * Producer:
   * 401/403 at the BYOK wire — 162 rows in three days, 93 of them on the last
   * one. Never collapsed with `provider_quota_exhausted`: the remedy is a RE-KEY,
   * and a box that waits out an invalid credential waits forever.
   *
   * THE STATUS IS ASKED SECOND, and the order is the whole correctness of this
   * arm. `providerFailureCode` tests the body for an exhaustion marker across ALL
   * 4xx before it reads 401/403, because a provider that refuses a SPENT QUOTA
   * with 403 was landing here — remedy "re-key", budget one attempt, both wrong
   * for a key that is fine and a window that clears on its own. The live
   * signature is kimi's weekly wall: 186 rows over the 4 days to 2026-08-29,
   * growing 25 → 33 → 57 → 71. Same two symbols as
   * {@link TaskFailureKind.ProviderQuotaExhausted}: one grades, one writes.
   *
   * Budget:
   * NOT CHARGED, and the re-pend is HELD, like `ProviderQuotaExhausted` (plan 101 L3).
   * A retry cannot help by construction: the second attempt presents the credential
   * the first was refused for. That was the argument for ONE attempt. It is equally
   * the argument for a PARK, because a re-key is what clears it. Failing the task
   * made the operator who re-keys also re-fire every task the dead key killed. Now a
   * key turning `valid` releases them (`TaskHoldReason.ProviderKeyRefused`). A key
   * nobody replaces leaves the row visibly parked with its reason, never failed, at
   * one re-check per {@link PROVIDER_KEY_REFUSED_RECHECK_MS}.
   *
   * Babysit round:
   * The key was refused — prod's daily-limit refusal grades here too; clears on a key change or reset.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   *
   * Alert sentence:
   * The PROVIDER, not the task, is why the run ended (plan 101 L3). None of these is a
   * fact about the work, so none charges an attempt:
   *
   * - a refused KEY (`provider_quota_exhausted`, `provider_auth_invalid`) used to charge
   *   one attempt and fail, so a spent quota failed the whole backlog one task at a time
   *   (446 rows in 30 days, 227 on one account on 2026-09-05). It now re-pends PARKED on
   *   `provider_key_refused`, and {@link ladderPhrase} says the row waits for a key;
   * - `provider_transient` is an outage that outlasted the box's own retries. It
   *   re-pends uncharged, HELD on `provider_capacity` until the reset the provider
   *   dated, else on a backoff that grows with the uncharged streak (#6501).
   *
   * Each sentence says what the human does; the ladder clause says what the row does.
   */
  ProviderAuthInvalid: {
    value: 'provider_auth_invalid',
    ordinal: 27,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
    ),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.ProviderKeyRefused,
      backstopMs: PROVIDER_KEY_REFUSED_RECHECK_MS,
      honorsStatedReset: true,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The model provider refused the credential itself — it is invalid, revoked, or names an account that does not exist. Re-key it; nothing about this task is wrong.',
      unclaimed:
        'No box claimed this — the model credential was rejected before any box could dial the provider.',
      ran: 'The model provider refused the credential itself — it is invalid, revoked, or names an account that does not exist. Re-key it; nothing here clears on its own.',
    },
  },
  /**
   * THE REQUEST DID NOT FIT. The provider refused because the prompt exceeded
   * the model's context window — not a fault, not a wall to wait out, not a
   * credential: an oversized request, whose only remedies are compacting the
   * context or routing to a model with a bigger window.
   *
   * The deferred line in {@link RunnerError} taken up. That docblock left this
   * signature in the residual on purpose at ONE occurrence and named the
   * condition for revisiting it — a trend — so here is the trend, measured on
   * prod 2026-09-01 over `task_run_events` where `kind='error'`:
   *   2026-08-29  1 event    2026-08-30  16 events    2026-08-31  8 events
   * 26 events across 19 distinct tasks, in two live provider phrasings
   * ({@link hasLlmContextOverflowMarker} documents each marker with its count,
   * including the two that carry no prod row today and why they are in anyway).
   *
   * ITS REAL COST WAS NEVER THE LABEL — IT WAS THE LADDER, and this is the one
   * number that decides the budget. Of the 19 tasks, **18 are settled and all 18
   * are `failed` at `attempt = 3`**; the 19th is still `in_progress` on attempt 2,
   * i.e. already twice failed. ZERO completed, zero merged — not a sample with a
   * weak tail but a clean sweep, and the deterministic signature #2572 used to
   * tighten six kinds to a single attempt. So: `Charged`, `maxAttempts: 1`,
   * `Fail` — the request the second attempt would send is the request the first
   * was refused for, and attempts two and three are guaranteed-doomed paid box
   * time on a customer's BYOK key. The rollback, exactly as #2572's, is reverting
   * the one line in {@link TASK_FAILURE_BUDGET}: if the post-deploy terminal-rate
   * read shows a retry-recovering population, this budget was wrong and the label
   * still is not.
   *
   * THE LABEL REACHES FEWER ROWS THAN THE FAULT DOES, and that is measured rather
   * than glossed, because it is the honest limit of what this member buys. The
   * overflow is the LAST recorded error on only 5 of the 19 — so today those
   * tasks' terminal `failure_kind` spreads across six members (`runner_error` 11,
   * `tool_unavailable` 2, `verify_failed` 2, `no_pr_opened`, `run_killed`,
   * `model_produced_no_work`), and a kind graded off the TERMINAL error alone
   * would name only that 5. The budget is what closes the gap: at `maxAttempts:
   * 1` the FIRST overflow that ends an attempt ends the task, so the row is named
   * by the fault that actually stopped it instead of by whichever seam threw two
   * attempts later. Stated so the next reader checks the right thing — the
   * post-deploy question is whether this kind's count approaches the fault's, and
   * a large residual gap means the grader is missing an arrival shape, not that
   * the fault went away.
   *
   * Distinct from every neighbouring provider arm, and the neighbours are what
   * make it worth naming: {@link ProviderQuotaExhausted} is spent (wait or pay),
   * {@link ProviderRateLimit} is paced (wait), {@link ProviderAuthInvalid} is
   * dead (re-key), {@link ProviderError} is the endpoint's own fault (wait), and
   * {@link ProviderNonAnswer} is a 200 that answered wrongly. This one is a
   * well-formed refusal of a well-formed request that was simply too big, and it
   * is the only member of the family whose remedy is OURS rather than the
   * operator's.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}), graded from
   * the provider's own text through {@link hasLlmContextOverflowMarker} — the
   * platform's one shared vocabulary for this fact, in `@developerz/domain`
   * beside the exhaustion markers, never a second matcher on the runner.
   *
   * Producer:
   * The same two symbols and the same division of labour as the quota arms —
   * `providerFailureCode` grades, `stampProviderFailure` writes — because it is
   * the same wire and every lane that meets a provider refusal already routes
   * through them.
   *
   * BOTH ARMS OF THE GRADER, and that is not symmetry for its own sake: the
   * DOMINANT signature has no HTTP status at all. `invalid params, context window
   * exceeds limit (2013)` — 20 of the 26 measured events — arrives as a planner
   * error's own `message` with nothing for `providerStatus` to read, so a grader
   * that only asked the 4xx-body question would ship a kind that could not reach
   * its own largest population. That is the `git_auth` failure mode this table
   * exists to prevent, and it would have passed the producer reachability check (`../../task.test.ts`).
   *
   * Budget:
   * ONE ATTEMPT, and this is the ONLY line of the split that changes spend — so
   * it is the only line that needed evidence beyond "the label was wrong".
   *
   * MEASURED, 2026-09-01: of the 18 tasks carrying a context-overflow error
   * event, **18 are `failed` at `attempt = 3`**. Every one climbed the full
   * `TASK_MAX_ATTEMPTS` ladder and every one still died — a clean sweep, not a
   * majority. Attempts two and three re-send the request attempt one was refused
   * for, on a customer's BYOK key, to reach the outcome already reached. That is
   * the same deterministic argument #2572 made for its six kinds, resting here on
   * a stronger observation than any of them had: those were argued from mechanism
   * with zero rows, this one has a population that ran the ladder to exhaustion
   * without a single recovery.
   *
   * CHARGED, not `Uncharged`, and the reason is the #2567 loop reached through
   * one more door: the tokens up to the refusal were billed, and `Uncharged`
   * re-queues without bound unless {@link TaskHoldReason.NoUsableKeys} paces it —
   * which it cannot, because the key is `valid` and the request size, not the
   * credential, is what was refused.
   *
   * Rollback is reverting THIS entry, exactly as #2572's: if the post-deploy
   * terminal-rate read at comparable volume (`scripts/task-failure-report.ts`)
   * shows a retry-recovering population, the budget was wrong — the LABEL still
   * is not, and the two are separable precisely because they are separate lines.
   *
   * Babysit round:
   * The request did not fit the model's window. UNCHARGED by the #4289 owner ruling
   * (the conflict ladder's original member; one table, one verdict). A brief that
   * overflows deterministically repeats until the `maxSweeps` ceiling ends it.
   *
   * Box-at-fault:
   * The request did not FIT. A property of the prompt we built and the model
   * we chose, identical on every box in the fleet — so paging a machine sends
   * an operator to inspect hardware that is working perfectly. It is also the
   * one member of the provider family whose remedy is OURS (narrow the task,
   * or route to a larger window) rather than the account holder's, which is a
   * reason to surface it on the task and still not on the box.
   */
  ContextWindowExceeded: {
    value: 'context_window_exceeded',
    ordinal: 28,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The request was bigger than the model’s context window, so the provider refused it. The account’s key is fine and nothing on the box is broken — narrow the task, or point this lane at a model with a larger window.',
      unclaimed:
        'No box claimed this — the request was already larger than the model’s context window before any run started.',
      ran: 'The request was bigger than the model’s context window, so the provider refused it. Nothing is broken and nothing is spent — the account’s key is fine. Narrow the task, or point this lane at a model with a larger window.',
    },
  },
  /**
   * THE PROVIDER ANSWERED IN PROSE WHERE THE PROTOCOL EXPECTED A CODE: a retired or
   * withdrawn model whose endpoint refuses every call with a farewell paragraph
   * instead of an error code. Measured on prod: **555 error events in the 7 days
   * to 2026-09-02, every one uncoded and filed `runner_error`**, all from
   * `stealth/ox-alpha` on openrouter (the `run_provenance` on 609 of them), each
   * `native_harness_failed` 84 ms after `native_harness_started` — no turn, no
   * tool call, an immediate refusal carrying *"Thank you for participating in the
   * Stealth Ox Alpha testing period. This model was ZAI's GLM-5.3 Flash…"* as the
   * error's own message. The last one was 2026-08-28; the population stopped when
   * the model left the lane, and the kind exists so the NEXT withdrawn alias is
   * counted under its cause rather than under the box.
   *
   * Three neighbours, and the borders are the whole point of a fourth member:
   *   {@link ProviderNonAnswer}    — the body was not the wire's PROTOCOL at all
   *                                  (HTML, a gateway page): structural, no prose read.
   *   {@link ModelProducedNoWork}  — a real completion, on protocol, that held no
   *                                  work: the 200 case of the same farewell.
   *   {@link ProviderError}        — the endpoint said IT failed (5xx).
   *   `provider_prose_answer`      — a refusal whose only diagnostic content is
   *                                  vendor copy; the status (when there is one) is a
   *                                  4xx nothing else claims, or there is no status.
   *
   * GRADED FROM THE PROVIDER'S OWN TEXT through {@link hasProviderProseAnswerMarker}
   * — a marker set, NOT a general free-text heuristic, and the same discipline the
   * exhaustion and context-overflow sets keep: one line per phrasing a provider we
   * dial has actually served, asked by the runner's `providerFailureCode` AFTER every
   * status-bearing arm, so a status the provider SET always outranks the shape of
   * its prose. `packages/agent`'s non-answer guard deliberately keeps NO phrase list
   * (a rewording passes it); this member does not contradict that — it grades the
   * failure AFTER it happened for the budget and the operator, where a miss costs a
   * label and never a benched model. The auto-demotion half is not here and will
   * not be.
   *
   * Budgeted IDENTICALLY to {@link RunnerError} — the split changes the LABEL, never
   * the spend (`task-failure.test.ts` pins the parity). A retired model will refuse attempt
   * two exactly as it refused attempt one, so a one-attempt budget is the honest
   * next step; it belongs in its own diff with a post-deploy terminal-rate read,
   * the way #2572 did it.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * The withdrawn-model farewell (555 rows / 7d, every one `runner_error`). Graded
   * from the provider's text through `hasProviderProseAnswerMarker`, asked LAST so a
   * status the provider set always wins over the shape of its prose.
   *
   * Budget:
   * TODAY'S budget carried over — the split changes the label, never the spend
   * (`task-failure.test.ts` pins the parity with `runner_error`). A retired model refuses
   * attempt two exactly as it refused attempt one, so ONE attempt is the honest
   * next step; it lands in its own diff with a post-deploy terminal-rate read.
   *
   * Babysit round:
   * A withdrawn model's farewell prose instead of an error code — no turn ran.
   *
   * Box-at-fault:
   * A withdrawn or alpha model answered with a notice in the success shape
   * (plan 101 slice 07). The provider served prose; the box did everything
   * asked of it. A model choice, never a machine's health.
   */
  ProviderProseAnswer: {
    value: 'provider_prose_answer',
    ordinal: 29,
    producer: wire(
      'packages/agent/src/providers/grade.ts#providerFailureCode',
      'packages/agent/src/providers/grade.ts#stampProviderFailure',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The model provider answered with prose in the success shape instead of a result — a withdrawn or alpha model served a notice, not an answer. Point this lane at a model that still exists.',
      unclaimed:
        'No box claimed this — the model provider that would have served it answered with a retired-model notice before any run started.',
      ran: 'The model provider answered with prose in the success shape instead of a result — a withdrawn or alpha model served a notice, not an answer. Nothing on the box is wrong; point this lane at a model that still exists.',
    },
  },
  /**
   * THE POOL KEY THIS RUN WAS HANDED IS SPENT — a quota or plan ceiling the box
   * cannot wait out on that credential. Raised only on a SEALED pool key — there is a
   * key to name; whether a sibling exists is the mothership's to find when it rotates:
   * a box on its own `DZ_BACKEND_*` env has nothing to cool and nowhere to rotate,
   * and keeps its own grade.
   *
   * IT IS {@link ProviderQuotaExhausted}'S CAUSE WITH A ROTATION ATTACHED, and
   * that one difference is the whole budget. `keyFailureReasonOf`
   * (`apps/runner/src/modes/worker/review-brain/failure-class.ts`) returns
   * non-null for exactly one grade — `credits_exhausted`, which
   * `providerFailureCode` lifts to `provider_quota_exhausted` — so this throw
   * fires on the wallet class and on nothing else. A plain 429 grades
   * `rate_limited` there and never reaches here.
   *
   * NOT CHARGED, and the three-way comparison is the justification:
   *
   *   - {@link ProviderRateLimit} is `Uncharged` and UNBOUNDED because the box's
   *     own cooldown (#3050) paces the re-pend.
   *   - {@link ProviderQuotaExhausted} is `Uncharged` and PARKED since plan 101
   *     L3. The key is still `valid` to the holder, so an immediate re-pend would
   *     land straight back on the credential that was just refused, and the
   *     `provider_key_refused` hold is what keeps that re-pend from being immediate.
   *   - THIS KIND ROTATES instead of waiting, on the one lane where the assigner
   *     half exists. The throw is preceded by an AWAITED `key_failed` status emit
   *     (`reassign: false`) that cools the key and puts it in THIS TASK'S skip
   *     set, so the next attempt cannot resolve onto it — the re-pend runs on a
   *     different credential by construction, not by hope. Nothing about the work
   *     was graded, so the attempt counter, which bounds *"the fix didn't
   *     work"*, has no fix to count.
   *
   * `maxConsecutive: 3` IS THE BOUND, and it is required rather than inherited:
   * the pacer here DRAINS a finite pool instead of lapsing on a clock, and a
   * cooled key's cooldown does lapse, so the same key can be re-offered to the
   * same task later. Three of these in a row means three separate pool keys
   * refused on a wall no wait clears — that is an ACCOUNT-wide wallet condition,
   * not a key-level one, and the remedy is a human funding it, never a fourth
   * rotation. The task ends `refusals_exhausted` at ZERO attempts spent, which is
   * both readable and re-fireable once the wallet is topped up.
   *
   * NO BOX-FAULT EXCLUSION: the fault is the CREDENTIAL, not the box. The
   * rotation is the task's key skip set, so any box resolves onto a sibling key
   * and excluding the refusing one would shrink the pool for no reason — the same
   * reading {@link RunFutile} takes.
   *
   * Produced at `apps/runner/src/llm/spent-pool-key.ts#SpentPoolKeyError` — thrown by the
   * scout plan call, the harness lanes (plan 102 slice 07a) and the review lane's terminal
   * (`apps/runner/src/modes/worker/review/pipeline/key-wall.ts`, #6228).
   *
   * Budget:
   * NOT CHARGED, like {@link ProviderQuotaExhausted}, but ROTATED rather than
   * parked, because the assigner half EXISTS on this lane: the throw is preceded by an awaited `key_failed` emit
   * (`reassign: false`) that cools the key and puts it in THIS TASK'S skip set, so
   * the next attempt resolves onto a sibling credential by construction. Nothing
   * about the work was graded — only the wallet behind one key ran out — so the
   * attempt counter, which bounds "the fix didn't work", has no fix to count.
   *
   * The cause is `provider_quota_exhausted`'s exactly: `keyFailureReasonOf`
   * (`apps/runner/src/modes/worker/review-brain/failure-class.ts`) returns non-null
   * only for `credits_exhausted`. A plain 429 grades `rate_limited` there and never
   * reaches this throw, so this kind is never a throttle wearing a wallet's name.
   *
   * `maxConsecutive: 3` IS THE BOUND, chosen rather than inherited. The pacer here
   * DRAINS a finite pool instead of lapsing on a clock, and a cooled key's cooldown
   * does lapse, so the same key can be re-offered to the same task later. Three in a
   * row means three separate pool keys refused on a wall no wait clears: that is an
   * ACCOUNT-wide wallet condition and the remedy is a human funding it, never a
   * fourth rotation. `refusals_exhausted` at zero attempts spent is readable and
   * re-fireable once it is funded.
   *
   * NOT box-local (`taskFailureBoxLocal`): the fault is the CREDENTIAL. Any box
   * resolves onto a sibling key, so excluding the refusing box would shrink the pool
   * for no reason — the reading `run_futile` takes.
   *
   * Babysit round:
   * This pool key's wallet is spent; a sibling key serves the next round.
   *
   * Box-at-fault:
   * The three kinds named on 2026-09-19, and all three answer NO — each for its own
   * reason, not as a batch. `PoolKeySpent` is the account's WALLET behind one pool
   * key: it is cooled and out of this task's rotation, so the next attempt runs on
   * a sibling from any box — `ProviderQuotaExhausted`'s posture, reached through the
   * pool door. `MaxOutputUnknown` is the model registry and the assignment's own
   * limits: every box in the fleet reads the same two, so provisioning the refusing
   * box changes nothing. `SurveyEmpty` is the ONE judgement call here, deliberately
   * the same NO: the identical silence is what a repository with nothing citable, or
   * a model that declines every scout question, produces, and the counts cannot
   * tell those apart — paging on that hypothesis is how a box alert stops being
   * read. Held with `taskFailureBoxLocal`'s refusal of the same kind
   * (`BOX_LOCAL_FAILURE_KINDS`), so the two seams state one reading.
   *
   * Alert sentence:
   * The three wire codes named on 2026-09-19. All three throw sites stamped `err.code`
   * from the day they landed and none of the codes was a `TaskFailureKind`, so every one
   * of these rows read as the RESIDUAL's sentence — "the worker box threw and no named
   * cause claimed it" — which was false in the one way that matters to the reader: the
   * box had named the cause precisely.
   *
   * Each sentence therefore does the one thing the residual's could not: it says whose
   * fault it is and what clears it. `survey_empty` is nobody's and re-surveys;
   * `pool_key_spent` is the account's wallet and charges nothing; `max_output_unknown` is
   * this platform's own model configuration and will not clear on a retry.
   *
   * `review_no_model` (plan 101 K4) joined the family: the review lane's refusal to post
   * a verdict no model reached. The row re-pends HELD (`review_awaiting_model`), so its
   * sentence mostly rides the transition trail, and it says what clears it.
   *
   * Reason sentence:
   * NEITHER ARM SAYS "no box claimed this", and that is the one thing to preserve
   * here. `refused` is `attempt === 0`, which `FailureKindCopy.unclaimed` (`./row.ts`) was once documented as
   * "refused before any box ever claimed it" — true of a CHARGED kind and false of
   * an UNCHARGED one, because `failureAttemptSql`
   * (`packages/db/src/queries/tasks/lifecycle.ts`) leaves `tasks.attempt` alone for
   * an uncharged budget. This throw comes from a run, i.e. AFTER a box claimed the
   * row and spent model calls on it, and on a first failure the counter
   * is still 0 — so the unclaimed sentence would be a flat lie about the one fact
   * the reader acts on. Both arms describe a run that HAPPENED; they differ only in
   * what the attempt counter beside them means. `disk_floor_unmet` (`DiskFloorUnmet`, `./box.ts`) takes the
   * same care ("No box GRADED the diff"), and `onboarding_tree_dirty` does not —
   * it is uncharged and its zero-attempt arm still claims nobody claimed the row.
   * Not fixed here because it is not this diff's line: the CAUSE is `refused` being
   * derived from a counter that an uncharged kind never moves, and the real repair
   * is a claim signal `failureReasonSentence` is given rather than infers. Caught by review
   * on PR #4551.
   */
  PoolKeySpent: {
    value: 'pool_key_spent',
    ordinal: 37,
    producer: wire('apps/runner/src/llm/spent-pool-key.ts#SpentPoolKeyError'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The inference key this run was handed is spent — a quota or plan ceiling no wait clears on that key. It was cooled and taken out of this task’s rotation, so the next attempt runs on a sibling key and no attempt was charged. Several of these in a row means the ACCOUNT is out of credit, not one key: top it up.',
      unclaimed:
        'The inference key this run was handed is spent — a quota or plan ceiling no amount of waiting clears on that key. NO attempt was charged, which is why the counter beside this still reads zero: the key has been cooled and taken out of this task’s rotation, and the next attempt runs on another key. Nothing about your repository or your ask is wrong. If several keys refuse in a row the account itself is out of credit: top it up, and the row can be retried.',
      ran: 'The inference key this run was handed is spent — a quota or plan ceiling no amount of waiting clears on that key. It has been cooled and taken out of this task’s rotation, so the next attempt runs on another key and NO attempt was charged for this failure — the attempts this row does carry were spent by earlier failures of some other kind. Nothing about your repository or your ask is wrong. If several keys refuse in a row the account itself is out of credit: top it up, and the row can be retried.',
    },
  },
  /**
   * NO PRODUCER SINCE 2026-09-25 (#5068) — kept because `tasks.failure_kind` rows carry
   * it. A missing ceiling is now answered with the model's window
   * (`unsourcedOutputCeiling`, `@developerz/agent`) instead of a refusal: the refusal
   * failed every task on a working model until someone wrote a table row (`k3`
   * 2026-09-18/19, `deepseek-v4.1-flash` 2026-09-25). What follows is the kind as it
   * was produced, and still grades a stored row.
   *
   * NOTHING SOURCED THE MODEL'S OUTPUT CEILING and the wire requires one. On the
   * anthropic wire `max_tokens` is mandatory, the SDK's silent default is 4,096,
   * and shipping that would truncate a run's every turn at a number nobody chose
   * — so `outputAskFor` refuses and the native harness throws the refusal BEFORE
   * ANY REQUEST IS SENT.
   *
   * A CONFIGURATION FAULT, NOT A WORK FAULT, and it is deterministic in the
   * strongest sense available: the inputs are the model id, the assignment's
   * `model_limits` and the box's env, all three identical on the next attempt.
   * Zero provider contact happened, so no token was billed and nothing about the
   * diff was graded. The remedy is named in the refusal's own message and every
   * branch of it is a human or a platform edit — re-verify the key so the
   * provider's `/models` reports the limit, add the model with
   * `maxOutputTokens` to `MODEL_REGISTRY`, extend `OUTPUT_CEILINGS`, or set the
   * max-output env var. None of those happens between two redeliveries.
   *
   * ONE ATTEMPT, `Fail` on exhaustion — the shape {@link ProviderAuthInvalid}
   * takes, for its reason verbatim: a retry cannot help BY CONSTRUCTION, because
   * attempt two asks the same question of the same registry and is refused at the
   * same line. A three-attempt ladder here is not a retry policy, it is three
   * claims, three checkouts and three teardowns bought to re-derive a refusal
   * that never touched a provider. `Terminal` would be
   * defensible and is deliberately not used, so the row still travels the
   * ordinary ladder machinery and an operator who fixes the registry mid-flight
   * is not fighting a special case.
   *
   * NOT `Uncharged`, which is the other zero-attempt arm and is wrong in the
   * dangerous direction: uncharged means re-enter the queue, and no box clears
   * this condition — unlike {@link BoxBaselineRed} or {@link DiskFloorUnmet},
   * where the refusal is one box's disk and the next box may be fine. Here every
   * box reads the same registry, so an uncharged re-queue is an unbounded loop of
   * claims deriving one identical refusal. For the same reason there is NO
   * box-fault exclusion: the fault is not box-local, and excluding the refusing
   * box would shrink the pool while changing nothing.
   *
   * TWO PRODUCERS, ONE KIND, and #3164's lesson is why that needs saying. That
   * lesson is that two producers with OPPOSITE retry semantics need two kinds —
   * it is not a rule against sharing. Here `outputAskFor`'s `refused` verdict and
   * the harness throw that carries it are one condition with one remedy, so they
   * share this member. THE KNOWN THIRD SITE, pinned rather than hidden:
   * `apps/runner/src/structured-object.ts#structuredOutputAsk` meets the SAME
   * `refused` verdict and throws a bare `Error` with no `code`, so it still files
   * in the residual. It is not stamped here because its arm is fused with
   * `no_room` — a live-window condition a compaction DOES clear, i.e. the
   * opposite retry semantics #3164 is actually about — and splitting those two
   * arms is its own change.
   *
   * Was produced by the native harness's throw, `outputAskFor`'s `refused` verdict and,
   * from 2026-09-20, the structured-object lane's throw — all three deleted with the
   * refusal.
   *
   * THAT THIRD ONE IS WHY A COMPLETE-LOOKING REGISTRY IS NOT A GUARANTEE. The two
   * producers named in this docblock were declared and the reachability check was green, while the
   * structured-object lane threw `outputAskFor`'s MESSAGE from a bare `Error` and
   * kept its code: 191 terminal events across 92 tasks between 2026-09-18 22:38
   * and 2026-09-19 15:27 UTC filed as the residual `runner_error`, each spending
   * the full three-attempt ladder on inputs identical on every attempt — against
   * the single attempt this kind exists to buy. A registry entry proves a producer
   * STAMPS; nothing in it proves every consumer FORWARDS.
   *
   * Producer:
   * Its three throw sites were deleted with the refusal (#5068): an unsourced ceiling on
   * the anthropic wire now asks the model's window. The value stays graded for stored rows.
   *
   * Budget:
   * ONE attempt, `Fail` — the {@link ProviderAuthInvalid} shape, for its reason
   * verbatim: a retry cannot help BY CONSTRUCTION. The refusal is thrown before any
   * request, off three inputs that are identical on the next attempt (the model id,
   * the assignment's `model_limits`, the box's env), so attempt two asks the same
   * question of the same registry and is refused at the same line. A three-attempt
   * ladder here buys three claims, three checkouts and three teardowns to re-derive
   * a refusal that never touched a provider. That is the whole leak.
   *
   * NOT `Uncharged`, the other zero-attempt arm, and this is where it differs from
   * `box_baseline_red` / `disk_floor_unmet`: those refusals are ONE BOX'S disk and
   * the next box may be fine, which is what makes an uncharged re-queue worth
   * spending. Every box reads the same model registry, so here it is an unbounded
   * loop of claims deriving one identical refusal. Same reason it is not box-local:
   * the fault is not the box's, and an exclusion would shrink the pool and change
   * nothing. `Terminal` would be defensible and is deliberately not used, so the row
   * travels the ordinary ladder machinery and an operator who fixes the registry
   * mid-flight is not fighting a special case.
   *
   * Babysit round:
   * No sourced output ceiling — the harness refuses before any request; a platform-config fault.
   *
   * Box-at-fault:
   * The three kinds named on 2026-09-19, and all three answer NO — each for its own
   * reason, not as a batch. `PoolKeySpent` is the account's WALLET behind one pool
   * key: it is cooled and out of this task's rotation, so the next attempt runs on
   * a sibling from any box — `ProviderQuotaExhausted`'s posture, reached through the
   * pool door. `MaxOutputUnknown` is the model registry and the assignment's own
   * limits: every box in the fleet reads the same two, so provisioning the refusing
   * box changes nothing. `SurveyEmpty` is the ONE judgement call here, deliberately
   * the same NO: the identical silence is what a repository with nothing citable, or
   * a model that declines every scout question, produces, and the counts cannot
   * tell those apart — paging on that hypothesis is how a box alert stops being
   * read. Held with `taskFailureBoxLocal`'s refusal of the same kind
   * (`BOX_LOCAL_FAILURE_KINDS`), so the two seams state one reading.
   *
   * Alert sentence:
   * The three wire codes named on 2026-09-19. All three throw sites stamped `err.code`
   * from the day they landed and none of the codes was a `TaskFailureKind`, so every one
   * of these rows read as the RESIDUAL's sentence — "the worker box threw and no named
   * cause claimed it" — which was false in the one way that matters to the reader: the
   * box had named the cause precisely.
   *
   * Each sentence therefore does the one thing the residual's could not: it says whose
   * fault it is and what clears it. `survey_empty` is nobody's and re-surveys;
   * `pool_key_spent` is the account's wallet and charges nothing; `max_output_unknown` is
   * this platform's own model configuration and will not clear on a retry.
   *
   * `review_no_model` (plan 101 K4) joined the family: the review lane's refusal to post
   * a verdict no model reached. The row re-pends HELD (`review_awaiting_model`), so its
   * sentence mostly rides the transition trail, and it says what clears it.
   */
  MaxOutputUnknown: {
    value: 'max_output_unknown',
    ordinal: 38,
    producer: wireUnproduced(
      'no producer since 2026-09-25 (#5068): `outputAskFor` (apps/runner/src/llm/output-ceiling.ts) and `outputAskForWire` (packages/agent/src/providers/output-ask.ts) ask the model window via `unsourcedOutputCeiling` where nothing sourced a ceiling, so nothing refuses; `grep -rn max_output_unknown apps packages --include=*.ts` finds no `code:` assignment',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'Nothing sourced the model’s maximum output size and this provider requires it on every request, so the run refused before sending anything rather than riding a default that truncates every answer. No tokens were spent and nothing was graded. Re-verify the key so the provider’s model list reports the limit, register the model’s limits, or point the lane at a model whose limits are known — a retry refuses identically.',
      unclaimed:
        'No box claimed this — the model chosen for this lane has no known output limit, and the provider it speaks to requires one.',
      ran: 'Nothing could tell us the model’s maximum output size, and this provider requires that number on every request — so the run refused before sending anything rather than silently using a default that would cut every answer short. No tokens were spent and nothing about your change was graded. Re-verify the provider key so its model list reports the limit, or choose a model whose limits we already know; retrying as-is refuses identically.',
    },
  },
  /**
   * A REVIEW REFUSED TO RUN WITH NO MODEL behind it (plan 101 K4). The box resolved
   * no brain for the review — no sealed credential, or one it could not open — and
   * refused BEFORE posting anything to the pull request: a review lane has no
   * box-env floor the way a coding run does, and the mechanical "floor review" it
   * used to post read as a real verdict (136 of 160 reviews in 24h on 2026-09-21).
   *
   * AN ACCOUNT CONDITION, NOT A WORK FAULT: nothing about the diff was graded and
   * no token was spent. What clears it is a KEY — restored, verified, topped up,
   * re-designated — never a retry. So it is `Uncharged` and re-pends HELD on
   * `TaskHoldReason.ReviewAwaitingModel` (`taskFailureRependHold`, `./failure-budget`), the same hold
   * the dispatcher stamps when it refuses to send a review out keyless in the first
   * place; this kind is the box-side belt for the race where the credential died
   * between the seal and the run.
   *
   * Produced at `apps/runner/src/modes/worker/review/pipeline/model-gate.ts#ReviewNoModelError`.
   *
   * Producer:
   * Plan 101 K4: the review lane's refusal to run with no model. Until this entry the
   * code matched nothing here and graded as the charged residual `runner_error`.
   *
   * Budget:
   * NOT CHARGED and UNBOUNDED (plan 101 K4): an account with no model is waiting for
   * a key, and a counter of "the fix didn't work" must not be spent by "no fix could
   * be attempted". The hot loop is bounded by the PARK, not a cap: the re-pend lands
   * held ({@link taskFailureRependHold}), so the row is not re-claimed until a key
   * change releases it or {@link REVIEW_NO_MODEL_RECHECK_MS} lapses.
   *
   * Babysit round:
   * No model resolved for the account — a key condition, nothing graded.
   *
   * Box-at-fault:
   * Plan 101 K4: the ACCOUNT has no model for the review lane; any box refuses the same,
   * so paging a machine sends an operator to healthy hardware (the account-side signals
   * carry the remedy). Plan 101 02-2: the PLATFORM's relay could not deliver the push —
   * the box never saw it, and its fleet link and the payload are the platform's to fix.
   *
   * Alert sentence:
   * The three wire codes named on 2026-09-19. All three throw sites stamped `err.code`
   * from the day they landed and none of the codes was a `TaskFailureKind`, so every one
   * of these rows read as the RESIDUAL's sentence — "the worker box threw and no named
   * cause claimed it" — which was false in the one way that matters to the reader: the
   * box had named the cause precisely.
   *
   * Each sentence therefore does the one thing the residual's could not: it says whose
   * fault it is and what clears it. `survey_empty` is nobody's and re-surveys;
   * `pool_key_spent` is the account's wallet and charges nothing; `max_output_unknown` is
   * this platform's own model configuration and will not clear on a retry.
   *
   * `review_no_model` (plan 101 K4) joined the family: the review lane's refusal to post
   * a verdict no model reached. The row re-pends HELD (`review_awaiting_model`), so its
   * sentence mostly rides the transition trail, and it says what clears it.
   *
   * Reason sentence:
   * Plan 101 K4. UNCHARGED, so — as with `PoolKeySpent` — a zero counter
   * does not mean no box claimed it: a box DID claim the review and refused it.
   * Both arms describe that refusal; they differ only in what the counter means.
   */
  ReviewNoModel: {
    value: 'review_no_model',
    ordinal: 39,
    producer: wire('apps/runner/src/modes/worker/review/pipeline/model-gate.ts#ReviewNoModelError'),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.ReviewAwaitingModel,
      backstopMs: REVIEW_NO_MODEL_RECHECK_MS,
      honorsStatedReset: false,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The review refused to run because no model could serve it — the account has no key that is valid, funded and approved for the review lane — so nothing was posted to the pull request and no attempt was charged. It waits, held, and runs as soon as a key is verified or restored.',
      unclaimed:
        'The box that picked up this review refused it: no model is configured for reviews on this account, so there was nothing to review with. NO attempt was charged, which is why the counter beside this reads zero, and the review is parked, not failed — it is rechecked on a clock and runs once a review model is available. Add or re-verify a provider key, or choose a review model.',
      ran: 'The box that picked up this review refused it: no model is configured for reviews on this account, so there was nothing to review with. NO attempt was charged for this refusal — the attempts this row carries were spent by earlier failures of some other kind — and the review is parked, not failed: it is rechecked on a clock and runs once a review model is available. Add or re-verify a provider key, or choose a review model.',
    },
  },
  /**
   * THE PROVIDER ANSWERED ONLY TRANSIENTLY (5xx, undated 429, dropped connection) past
   * the box's in-step retries; not the work's fault (plan 101 G2). Nothing about the
   * diff was graded: the provider's AVAILABILITY failed, and waiting is what clears it.
   *
   * Before this kind the exhaustion graded {@link ProviderError} (a 5xx) or fell to the
   * {@link RunnerError} residual (an undated 429), and both spent a CHARGED attempt on an
   * outage. So it is `Uncharged`, and paced exactly as {@link ProviderRateLimit} is: the
   * box that graded it holds its own pulls for the provider's `retry-after`
   * (`apps/runner/src/backend-cooldown.ts` `holdForFailure`). Distinct from
   * {@link ProviderRateLimit}, which is a DATED throttle the router quoted a reset for.
   * This kind is the undated remainder.
   *
   * Produced at `apps/runner/src/llm/transient-exhaustion.ts#ProviderTransientExhaustedError`.
   *
   * Producer:
   * Plan 101 G2: the box's in-step retries ran out on transient answers only.
   *
   * Budget:
   * NOT CHARGED and UNBOUNDED (plan 101 G2), the {@link ProviderRateLimit} posture and
   * for its reason: the provider's AVAILABILITY failed, not the work, and the attempt
   * counter bounds *"the fix didn't work"*. The hot loop is bounded by the PACER, not a
   * cap: the box that graded it holds its own pulls for the provider's `retry-after`
   * (`apps/runner/src/backend-cooldown.ts` `holdForFailure`). No `maxConsecutive`: a
   * count of outages in a row would end work on how long a provider stayed down, the
   * leash the 2026-09-16 ruling removed.
   *
   * Park:
   * HELD on `provider_capacity` (#6501, 2026-10-19), the dated throttle's own hold and its
   * own wakes — the clock, and a route change (`releaseHoldsOnRouteChange`). The box's
   * pull hold paced only the box that graded it, so the re-pend was claimable at once by
   * every OTHER box: 2026-10-11..18, five tesote tasks re-ran 1,699 times, one every
   * ~50 s at `uncharged_streak` up to 384, each failing `gpt-6.1-sol` HTTP 429 with the
   * router's own "earliest reset 2026-10-22". So: a refusal that DATES its reset parks
   * until then (`honorsStatedReset`, read by `apps/worker/src/fleet/stated-key-reset.ts`);
   * one that does not backs off on the uncharged streak, from
   * `PROVIDER_TRANSIENT_BACKOFF_BASE_MS` doubling to `PROVIDER_TRANSIENT_BACKOFF_CAP_MS`.
   * Still uncharged and unbounded: the park paces the re-try, it never ends the task.
   *
   * Babysit round:
   * 5xx / undated 429 past the in-step retries — provider availability.
   *
   * Box-at-fault:
   * Account and credential conditions — the provider family and `GitAuth` (`./tree.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   *
   * Alert sentence:
   * The PROVIDER, not the task, is why the run ended (plan 101 L3). None of these is a
   * fact about the work, so none charges an attempt:
   *
   * - a refused KEY (`provider_quota_exhausted`, `provider_auth_invalid`) used to charge
   *   one attempt and fail, so a spent quota failed the whole backlog one task at a time
   *   (446 rows in 30 days, 227 on one account on 2026-09-05). It now re-pends PARKED on
   *   `provider_key_refused`, and {@link ladderPhrase} says the row waits for a key;
   * - `provider_transient` is an outage that outlasted the box's own retries. It
   *   re-pends uncharged, HELD on `provider_capacity` until the reset the provider
   *   dated, else on a backoff that grows with the uncharged streak (#6501).
   *
   * Each sentence says what the human does; the ladder clause says what the row does.
   *
   * Reason sentence:
   * Plan 101: the provider stayed unavailable past the box's own retries. Uncharged —
   * the row re-enters the queue — so the sentence must not read as a failure to fix.
   */
  ProviderTransient: {
    value: 'provider_transient',
    ordinal: 40,
    producer: wire('apps/runner/src/llm/transient-exhaustion.ts#ProviderTransientExhaustedError'),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.ProviderCapacity,
      backstopMs: PROVIDER_TRANSIENT_BACKOFF_BASE_MS,
      honorsStatedReset: true,
      backoffCapMs: PROVIDER_TRANSIENT_BACKOFF_CAP_MS,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The model provider stayed unavailable past the box's retries. The task is held until the reset the provider stated, or on a wait that grows while the outage lasts; nothing about this task is wrong.",
      unclaimed:
        'No box claimed this — the model provider was unavailable when the row was offered, so the work waits in the queue until it answers again.',
      ran: 'The model provider stayed unavailable past the box’s retries, so the run stopped with no attempt charged and the work re-enters the queue held: until the reset the provider stated, or on a wait that grows while the outage lasts. Nothing on the box or in the repository is wrong; it clears when the provider recovers.',
    },
  },
  /**
   * NO MODEL KEY COULD BE USED FOR THIS SCOUT (#6765, R4: no fallback plan). The scout's
   * model gate answered `no_credential`: the assignment carries no sealed `llm_credential`
   * at all. Before R4 the scout authored a one-group floor plan that read like a healthy
   * run; now it ends failed with the reason ("the scout ends failed with the reason").
   *
   * Producer:
   * `ScoutRunFailedError` with reason `no_credential`, graded by `SCOUT_FAILURE_KIND`.
   *
   * Budget:
   * `review_no_model`'s family: no model ran, so nothing is charged. Bounded at three
   * refusals in a row (`uncharged(3)`), because nothing paces an uncharged re-queue on
   * this lane and the account's key does not appear by retrying.
   *
   * Babysit round:
   * Copied from `review_no_model` (`Uncharged`): no model ran.
   *
   * Box-at-fault:
   * No. The account brought no key; every box reads the same assignment.
   */
  ScoutNoCredential: {
    value: 'scout_no_credential',
    ordinal: 70,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The scout had no model key it could use for this account, so no model ran and no attempt was charged. Connect a provider (Settings, AI providers) and redispatch the task; it ends failed after three such refusals in a row.',
      unclaimed:
        'No box claimed this — the scout had no model key it could use for this account. Connect a provider (Settings, AI providers) and redispatch the task.',
      ran: 'No model key could be used for this scout. Connect a provider (Settings, AI providers) and redispatch the task. It ends failed after three such refusals in a row.',
    },
  },
} as const satisfies Record<string, FailureKindRow>;
